import logging from 'utils/logging';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  GAMESTATE_ROUNDSTATE_ROUND_COMPLETE,
} from '../../gamestate/constants';
import { MOMENT_TYPES, NARRATION_KEYS, HOST_STATUS } from '../hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { updateGameStateAction } from '../../gamestate/stateActions';
import { extractQuestionIdsForRound, areAllQuestionsPicked } from '../../gamestate/roundCompletionUtils';
import { selectAllQuestions } from '../../question/questionSlice';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.JudgingComplete');
log.setLevel('info');

// Track which questions have been judged to avoid duplicate triggers
const questionsJudged = new Set();

export const resetJudgingCompleteCache = () => {
  questionsJudged.clear();
};
registerCacheReset('round', resetJudgingCompleteCache);

const buildPostJudgingContext = ({
  currentPlayers = [],
  submissions = [],
  question,
  currentControlPlayerId,
}) => {
  const playersById = new Map(
    currentPlayers.map(player => [player.id, { ...player, score: player.score || 0 }])
  );
  const submissionByPlayerId = new Map(
    submissions.map(playerSubmission => [playerSubmission.id, playerSubmission])
  );

  if (question?.dailyDouble) {
    const ddSubmission = submissionByPlayerId.get(currentControlPlayerId);
    const ddPlayer = playersById.get(currentControlPlayerId);
    const wagerAmount = ddSubmission?.submission?.wager || 0;
    const outcome = ddSubmission?.submission?.status === 'correct' ? 'correct' : 'incorrect';
    const scoreBeforeDD = ddPlayer?.score || 0;
    const scoreAfterDD = outcome === 'correct'
      ? scoreBeforeDD + wagerAmount
      : scoreBeforeDD - wagerAmount;

    if (ddPlayer) {
      ddPlayer.score = scoreAfterDD;
    }

    const projectedScores = [...playersById.values()]
      .map(p => ({ id: p.id, score: p.score || 0 }))
      .sort((a, b) => b.score - a.score);
    const newRank = projectedScores.findIndex(p => p.id === currentControlPlayerId) + 1;
    const othersMax = currentPlayers
      .filter(p => p.id !== currentControlPlayerId)
      .reduce((max, p) => Math.max(max, p.score || 0), 0);

    return {
      players: [...playersById.values()],
      submissions: submissions.map(playerSubmission => {
        if (playerSubmission.id !== currentControlPlayerId) {
          return playerSubmission;
        }

        return {
          ...playerSubmission,
          score: scoreAfterDD,
          submission: {
            ...playerSubmission.submission,
            points: outcome === 'correct' ? wagerAmount : -wagerAmount,
          },
        };
      }),
      dailyDoubleContext: {
        wagerAmount,
        outcome,
        scoreBeforeDD,
        scoreAfterDD,
        newRank,
        tookLead: scoreAfterDD > othersMax && scoreBeforeDD <= othersMax,
        correctAnswer: question.answers?.[0] || question.answer || null,
      },
    };
  }

  const correctSubmissions = submissions
    .filter(playerSubmission => playerSubmission.submission?.status === 'correct')
    .filter(playerSubmission => playerSubmission.submission?.submittedAt)
    .sort((a, b) => new Date(a.submission.submittedAt) - new Date(b.submission.submittedAt));
  const firstCorrectId = correctSubmissions[0]?.id || null;

  const projectedSubmissions = submissions.map(playerSubmission => {
    const player = playersById.get(playerSubmission.id);
    const status = playerSubmission.submission?.status;
    let points = playerSubmission.submission?.points || 0;

    if (status === 'correct' && playerSubmission.id === firstCorrectId) {
      points += 10;
    }

    if ((status === 'correct' || status === 'incorrect') && player) {
      player.score = (player.score || 0) + points;
    }

    return {
      ...playerSubmission,
      score: player?.score ?? playerSubmission.score ?? 0,
      submission: playerSubmission.submission
        ? {
            ...playerSubmission.submission,
            points,
          }
        : playerSubmission.submission,
    };
  });

  return {
    players: [...playersById.values()],
    submissions: projectedSubmissions,
    dailyDoubleContext: null,
  };
};

/**
 * Moment that triggers when all submissions for a question have been judged
 */
export const JudgingCompleteMoment = {
  id: 'JUDGING_COMPLETE',
  shouldTrigger: ({ prevState, currentState, currentPlayers, submissions, questions, gameObject }) => {
    // Only trigger in round state
    if (currentState?.currentState !== GAMESTATE_ROUND) {
      return false;
    }

    // Check if we're in the judging state
    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_JUDGING) {
      return false;
    }

    log.info(`JudgingComplete.shouldTrigger called in JUDGING state for question ${currentState?.questionId}`);

    // Check if we have a question ID
    const questionId = currentState?.questionId;
    if (!questionId) {
      return false;
    }

    // Check if this question has already been judged
    if (questionsJudged.has(questionId)) {
      return false;
    }

    // Check if we have submissions
    if (!submissions || submissions.length === 0) {
      log.debug('No submissions available for judging');
      return false;
    }

    // Debug: Log the submissions data
    log.info('JudgingComplete checking submissions:', submissions.map(s => ({
      name: s.name,
      hasSubmission: !!s.submission,
      answer: s.submission?.answer,
      status: s.submission?.status
    })));

    // Check if all submissions have been judged (correct/incorrect/committed)
    // Using a whitelist prevents 'wager' status (DD pre-answer) from being
    // treated as judged, which caused premature triggers with wrong outcome data
    const judgedStatuses = ['correct', 'incorrect'];
    const allJudged = submissions.every(playerSubmission => {
      // If player didn't submit anything, that's fine - not unjudged
      if (!playerSubmission || !playerSubmission.submission) {
        return true;
      }

      const status = playerSubmission.submission.status;
      if (status === undefined || status === null) {
        log.warn('Submission status is null/undefined:', playerSubmission.submission);
        return true;
      }

      return judgedStatuses.includes(status);
    });

    if (allJudged) {
      // Mark this question as judged to prevent duplicate triggers
      questionsJudged.add(questionId);
      
      log.info(`All submissions for question ${questionId} have been judged. Triggering action to advance state`);
      
      // Find the question object
      const question = questions?.find(q => q.id === questionId);
      if (!question) {
        log.error(`Question ${questionId} not found in questions array`);
        return false;
      }

      // Detect if this is the last question in the round
      let isLastQuestionInRound = false;
      if (gameObject && currentState.round) {
        const questionIdsInRound = extractQuestionIdsForRound(gameObject, currentState.round);
        const pickedStatus = questionIdsInRound.map(id => ({
          id,
          value: gameObject.questions?.[id]?.value,
          picked: gameObject.questions?.[id]?.picked,
          status: gameObject.questions?.[id]?.status
        }));
        const pickedCount = pickedStatus.filter(q => q.picked === true).length;
        isLastQuestionInRound = areAllQuestionsPicked(questionIdsInRound, questions);

        log.debug(`Round completion check: round=${currentState.round}, totalQuestions=${questionIdsInRound.length}, pickedCount=${pickedCount}, isLastQuestion=${isLastQuestionInRound}`);
        log.debug(`Questions picked status:`, pickedStatus);
      }

      // Get current control player information
      const currentControlPlayerId = currentState?.playerInControl;
      const currentControlPlayer = currentPlayers?.find(p => p.id === currentControlPlayerId);
      
      // Calculate who will be the next control player
      let nextControlPlayer = currentControlPlayer; // Default: current player retains control
      
      // If someone got it correct, fastest correct answer gets control
      const correctSubmissions = submissions
        .filter(playerSubmission => playerSubmission.submission?.status === 'correct')
        .filter(playerSubmission => playerSubmission.submission?.submittedAt)
        .sort((a, b) => new Date(a.submission.submittedAt) - new Date(b.submission.submittedAt));
      
      let firstCorrect = null;
      if (correctSubmissions.length > 0) {
        firstCorrect = correctSubmissions[0];
        nextControlPlayer = currentPlayers?.find(p => p.id === firstCorrect.id);
      }

      const postJudgingContext = buildPostJudgingContext({
        currentPlayers,
        submissions,
        question,
        currentControlPlayerId,
      });
      
      // Create narration params
      const narrationKey = `${NARRATION_KEYS.JUDGING_RESULTS}_${questionId}`;
      
      // In Final round, skip narration — the reveal sequence handles its own narration.
      // Still trigger the action to advance state (judging → final.revealing).
      const isFinalRound = currentState.round === GAMESTATE_ROUND_FINAL;

      return {
        shouldTriggerAction: true,
        shouldGenerateNarration: !isFinalRound,
        triggerAction: JudgingCompleteMoment.triggerAction,
        narrationParams: {
          narrationKey,
          momentType: MOMENT_TYPES.JUDGING_COMPLETE,
          context: {
            questionId,
            questionText: question.clue,
            category: question.category,
            value: question.value,
            dailyDouble: question.dailyDouble,
            round: currentState.round,
            isLastQuestionInRound,
            currentControlPlayer: currentControlPlayer ? {
              id: currentControlPlayer.id,
              name: currentControlPlayer.name
            } : null,
            nextControlPlayer: nextControlPlayer ? {
              id: nextControlPlayer.id,
              name: nextControlPlayer.name
            } : null,
            firstCorrect: firstCorrect ? {
              playerName: firstCorrect.name,
              timeToSubmit: (() => {
                if (firstCorrect.submission?.submittedAt && question.startTime) {
                  const submitTime = new Date(firstCorrect.submission.submittedAt);
                  const startTime = new Date(question.startTime);
                  const timeDiff = (submitTime - startTime) / 1000;
                  return timeDiff.toFixed(2);
                }
                return null;
              })()
            } : null,
            playerStandings: postJudgingContext.players
              ? [...postJudgingContext.players]
                  .sort((a, b) => (b.score || 0) - (a.score || 0))
                  .map(p => ({ name: p.name, score: p.score || 0 }))
              : null,
            submissions: postJudgingContext.submissions.map(playerSubmission => {
              // Calculate time-to-submit like the leaderboard does
              let timeToSubmit = null;
              if (playerSubmission.submission?.submittedAt && question.startTime) {
                const submitTime = new Date(playerSubmission.submission.submittedAt);
                const startTime = new Date(question.startTime);
                const timeDiff = (submitTime - startTime) / 1000;
                timeToSubmit = timeDiff.toFixed(2);
              }

              return {
                playerName: playerSubmission.name,
                answer: playerSubmission.submission?.answer || '',
                status: playerSubmission.submission?.status || 'no_submission',
                points: playerSubmission.submission?.points || 0,
                wager: playerSubmission.submission?.wager || null,
                playerScore: playerSubmission.score || 0,
                timeToSubmit: timeToSubmit
              };
            }),
            dailyDoubleContext: postJudgingContext.dailyDoubleContext,
          }
        }
      };
    }

    return false;
  },

  /**
   * Trigger action to advance the game state
   * @param {Object} gameObject - Game object from Redux state
   * @param {Array} players - Current players
   * @param {Function} dispatch - Redux dispatch function
   */
  triggerAction: async (gameObject, players, dispatch) => {
    log.info('Judging complete, advancing game state');

    try {
      if (!gameObject) {
        log.error('Game object not provided');
        return;
      }

      // Advance the game state using the existing logic
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing game state after judging complete:', error);
    }
  }
};

/**
 * Playback moment for judging results narration
 */
export const JudgingCompletePlaybackMoment = {
  id: 'JUDGING_COMPLETE_PLAYBACK',
  momentType: MOMENT_TYPES.JUDGING_COMPLETE,
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    // Check if narration exists
    if (!narration) {
      return false;
    }

    // Check if this is a judging results narration
    if (!narration.momentType || narration.momentType !== MOMENT_TYPES.JUDGING_COMPLETE) {
      return false;
    }

    // Check if we're in the results state
    const inResultsState = gameState?.currentState === GAMESTATE_ROUND && 
                          gameState?.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS;
    if (!inResultsState) {
      return false;
    }

    // Check if narration is ready to play
    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    if (!narrationReady) {
      return false;
    }

    // Check if host is available to speak (not already speaking)
    const hostAvailable = hostState?.status !== HOST_STATUS.SPEAKING;
    if (!hostAvailable) {
      return false;
    }

    // Check if this specific narration has been played before
    const notPlayedYet = !playedNarrations.has(narration.key);
    if (!notPlayedYet) {
      return false;
    }

    return true;
  },

  /**
   * Callback when the narration is complete
   * Advances the game state to the next phase
   */
  onComplete: async (gameId, dispatch, getState) => {
    log.debug('Judging results narration complete, advancing to next state');

    try {
      const state = getState();
      const gameObject = state.game?.game;

      if (!gameObject) {
        log.error('Game object not found in state');
        return;
      }

      const gameState = gameObject.state;
      const questions = selectAllQuestions(state);

      // Check if all questions in this round are picked (using Redux entities which have picked status)
      const questionIdsInRound = extractQuestionIdsForRound(gameObject, gameState.round);
      const isRoundComplete = areAllQuestionsPicked(questionIdsInRound, questions);

      log.info(`Round completion check: round=${gameState.round}, isRoundComplete=${isRoundComplete}, questionsInRound=${questionIdsInRound.length}`);

      if (isRoundComplete && gameState.round !== GAMESTATE_ROUND_FINAL) {
        // Round complete - transition to wrap-up narration state
        log.info(`All questions picked in ${gameState.round}, advancing to ROUND_COMPLETE`);
        dispatch(updateGameStateAction(gameId, {
          currentState: GAMESTATE_ROUND,
          round: gameState.round,
          roundState: GAMESTATE_ROUNDSTATE_ROUND_COMPLETE,
          questionId: null,
          playerInControl: gameState.playerInControl,
        }));
      } else {
        log.info("Judging results narration complete, advancing state");
        // More questions to go - back to waiting for next pick
        const players = Object.values(state.player?.entities || {});
        await advanceState(gameObject, players, dispatch);
      }
    } catch (error) {
      log.error('Error advancing game state after judging results narration:', error);
    }
  }
};

const api = {
  trigger: JudgingCompleteMoment,
  playback: JudgingCompletePlaybackMoment
};

export default api; 
